import React, { Children } from 'react';
import { BaseTable, BaseTableRenderProps } from '../base-table/base-table';
import { TableRowType } from '@make-software/cspr-design';
import { TableLoader } from './table-loader';
import { TableError } from './table-error';
import { ErrorResult } from 'app/organizations-api/types';
import { SortingProps } from '../pagination/types';
import Pagination from '../pagination/pagination';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

type RenderProps = BaseTableRenderProps & { sortingProps: SortingProps };

export interface DragNDropConfig<Entity> {
  getID: (entity: Entity) => string | number;
  onReorder?: (newOrder: Entity[]) => void;
}

export type TableProps<Entity> = {
  loading: boolean;
  error?: ErrorResult | null;
  data: null | Entity[];
  renderTableHeader?: (renderProps: RenderProps) => React.ReactElement;
  renderDataHeader: (renderProps: RenderProps) => React.ReactElement;
  renderPaginatedData: (
    paginatedData: Entity[],
    renderProps: RenderProps
  ) => React.ReactElement | React.ReactElement[];
  tableRowType?: TableRowType;
  handlePageActions: (page: number, limit: number) => void;
  pageCount: number;
  currentPage: number;
  pageSize?: number;
  itemCount?: number;
  dragNDropConfig?: DragNDropConfig<Entity>;
};

export const Table = <Entity extends any>({
  loading,
  data,
  renderTableHeader,
  renderDataHeader,
  renderPaginatedData,
  error,
  tableRowType = TableRowType.TextWithAvatar,
  handlePageActions,
  pageCount,
  currentPage,
  pageSize,
  itemCount,
  dragNDropConfig,
  ...props
}: TableProps<Entity>) => {
  const [sortingData, setSortingData] = React.useState<Entity[]>(data || []);
  React.useEffect(() => {
    setSortingData(data || []);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = sortingData.findIndex(
      (r) => String(dragNDropConfig?.getID(r)) === String(active.id)
    );
    const newIndex = sortingData.findIndex(
      (r) => String(dragNDropConfig?.getID(r)) === String(over.id)
    );
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(sortingData, oldIndex, newIndex);
    setSortingData(next);
    dragNDropConfig?.onReorder?.(next);
  };

  const renderFooter = () =>
    !error && (
      <Pagination
        itemCount={itemCount}
        pageCount={pageCount}
        currentPage={currentPage}
        pageLimit={pageSize}
        handlePageActions={handlePageActions}
      />
    );

  const renderData = (renderProps: BaseTableRenderProps) => {
    if ((data == null && !error) || loading) {
      return (
        <TableLoader
          columnsLength={Children.count(
            renderDataHeader({
              ...renderProps,
              sortingProps: {}!
            }).props.children
          )}
          tableRowType={tableRowType}
        />
      );
    }

    if (error) {
      return (
        <TableError
          columnsLength={Children.count(
            renderDataHeader({
              ...renderProps,
              sortingProps: {}!
            }).props.children
          )}
          error={error}
        />
      );
    }

    if (dragNDropConfig && sortingData) {
      return (
        <SortableContext
          items={sortingData.map((one) => dragNDropConfig.getID(one))}
          strategy={verticalListSortingStrategy}
        >
          {renderPaginatedData(sortingData, {
            ...renderProps,
            sortingProps: {}!
          })}
        </SortableContext>
      );
    }

    if (data) {
      return renderPaginatedData(data, {
        ...renderProps,
        sortingProps: {}!
      });
    }

    return <></>;
  };

  if (dragNDropConfig) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <BaseTable
          renderTableHeader={(renderProps: BaseTableRenderProps) =>
            renderTableHeader &&
            renderTableHeader({
              ...renderProps,
              sortingProps: null!
            })
          }
          renderDataHeaders={(renderProps) =>
            renderDataHeader({ ...renderProps, sortingProps: null! })
          }
          renderData={renderData}
          renderFooter={renderFooter}
          {...props}
        />
      </DndContext>
    );
  }

  return (
    <BaseTable
      renderTableHeader={(renderProps: BaseTableRenderProps) =>
        renderTableHeader &&
        renderTableHeader({
          ...renderProps,
          sortingProps: null!
        })
      }
      renderDataHeaders={(renderProps) => renderDataHeader({ ...renderProps, sortingProps: null! })}
      renderData={renderData}
      renderFooter={renderFooter}
      {...props}
    />
  );
};

export default Table;
